
// ---------------------------------------------------------------------------
// Merged from templatevariables.ts (case-conflict resolution)
// ---------------------------------------------------------------------------
/**
 * Menuraq Unified Template Variable System
 *
 * Canonical flat-key variable structure for colors, fonts, sizes, images, tokens.
 * - All templates SHOULD define variables using these canonical keys.
 * - Old/arbitrary keys are supported via runtime normalization (backward compat).
 * - CSS custom properties are injected on the root div so external components
 *   can read them without touching the template context.
 * - useTemplateTheme() hook exposes resolved variables + CSS var names to any
 *   component outside the renderer.
 */

import type { Template, TemplateThemeMode, Variables } from "../types/template";

// ---------------------------------------------------------------------------
// 1. CANONICAL VARIABLE SHAPE
// ---------------------------------------------------------------------------

/**
 * The canonical set of color keys every template should define.
 * Templates using old arbitrary keys will have them normalized here.
 */
export interface CanonicalColors {
  // Surface / chrome
  background: string;
  foreground: string;
  panel: string;
  panelBorder: string;
  divider: string;
  muted: string;
  mutedForeground: string;

  // Typography
  heading: string;
  subheading: string;
  body: string;

  // Semantic item colors
  itemTitle: string;
  itemDesc: string;
  sectionTitle: string;
  price: string;

  // Brand
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  accent: string;
  accentForeground: string;

  // Promo
  promoBg: string;
  promoText: string;
  promoBorder: string;

  // State
  destructive: string;
  destructiveForeground: string;
}

export interface CanonicalFonts {
  heading: string;
  subheading: string;
  body: string;
  section: string;
  item: string;
  price: string;
}

export interface CanonicalSizes {
  borderRadius: string;
  itemCardRadius: string;
  categoryRadius: string;
  coverRadius: string;
  maxContentWidth: string;
  pagePaddingX: string;
  pagePaddingY: string;
}

export interface CanonicalImages {
  coverBackground: string;
  logoUrl: string;
  patternUrl: string;
}

export interface CanonicalTokens {
  [key: string]: string | number;
}

/**
 * The fully resolved, normalized variables object.
 * Every renderer and external component should use this shape.
 */
export interface ResolvedTemplateVariables {
  colors: Partial<CanonicalColors> & Record<string, string>;
  fonts: Partial<CanonicalFonts> & Record<string, string>;
  sizes: Partial<CanonicalSizes> & Record<string, string | number>;
  images: Partial<CanonicalImages> & Record<string, string>;
  tokens: CanonicalTokens;
  // Seasonal skin (doc 03 §2/§5) — carried through so `${headline}` and the
  // renderer's `skin` context resolve. Written by `applyDesignOverrides`.
  skin?: string;
  headline?: string;
  skinDecor?: string;
}

// ---------------------------------------------------------------------------
// 2. OLD-KEY → CANONICAL-KEY NORMALIZATION MAPS
// ---------------------------------------------------------------------------

/**
 * Maps legacy / arbitrary color keys to their canonical equivalent.
 * Applied at runtime so old templates continue to work unchanged.
 *
 * Priority: canonical key wins over a legacy alias if both are present.
 */
const COLOR_ALIAS_MAP: Record<string, keyof CanonicalColors> = {
  // Common legacy aliases → canonical
  card: "panel",
  cardForeground: "foreground",
  cardBackground: "panel",
  surfaceBorder: "panelBorder",
  inputBackground: "panel",
  mutedText: "mutedForeground",
  textMuted: "mutedForeground",

  // Typography aliases
  text: "foreground",
  textPrimary: "heading",
  textSecondary: "subheading",
  textBody: "body",
  textHeading: "heading",
  textSubheading: "subheading",
  headerColor: "heading",
  header: "heading",

  // Item aliases
  itemColor: "itemTitle",
  itemText: "itemTitle",
  itemDescription: "itemDesc",
  descColor: "itemDesc",
  descText: "itemDesc",
  priceColor: "price",
  priceText: "price",

  // Section aliases
  sectionColor: "sectionTitle",
  sectionText: "sectionTitle",
  categoryTitle: "sectionTitle",
  categoryColor: "sectionTitle",

  // Brand aliases
  brandColor: "primary",
  brandPrimary: "primary",
  brandAccent: "accent",
  highlight: "accent",
  highlightText: "accentForeground",

  // Accent aliases
  accentText: "accentForeground",

  // Promo aliases
  promoBackground: "promoBg",
  promoBadgeBg: "promoBg",
  promoLabel: "promoText",
  promoBadgeText: "promoText",
  promoBadgeBorder: "promoBorder",

  // State aliases
  danger: "destructive",
  error: "destructive",
  errorForeground: "destructiveForeground",
};

const FONT_ALIAS_MAP: Record<string, keyof CanonicalFonts> = {
  // Legacy aliases → canonical
  header: "heading",
  headerFont: "heading",
  titleFont: "heading",
  subtitleFont: "subheading",
  textFont: "body",
  bodyFont: "body",
  copy: "body",
  sectionFont: "section",
  categoryFont: "section",
  itemFont: "item",
  menuItemFont: "item",
  priceFont: "price",
  costFont: "price",
};

// ---------------------------------------------------------------------------
// 3. NORMALIZATION LOGIC
// ---------------------------------------------------------------------------

function normalizeColorRecord(
  raw: Record<string, string> | undefined,
): ResolvedTemplateVariables["colors"] {
  if (!raw) return {};
  const out: ResolvedTemplateVariables["colors"] = {};

  // First pass: copy everything as-is (preserves canonical + unknown keys)
  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === "string" && value.trim()) {
      out[key] = value;
    }
  }

  // Second pass: apply alias map , only fill canonical key if not already set
  for (const [aliasKey, canonicalKey] of Object.entries(COLOR_ALIAS_MAP)) {
    if (aliasKey in out && !(canonicalKey in out)) {
      out[canonicalKey] = out[aliasKey]!;
    }
  }

  return out;
}

function normalizeFontRecord(
  raw: Record<string, string> | undefined,
): ResolvedTemplateVariables["fonts"] {
  if (!raw) return {};
  const out: ResolvedTemplateVariables["fonts"] = {};

  for (const [key, value] of Object.entries(raw)) {
    if (typeof value === "string" && value.trim()) {
      out[key] = value;
    }
  }

  for (const [aliasKey, canonicalKey] of Object.entries(FONT_ALIAS_MAP)) {
    if (aliasKey in out && !(canonicalKey in out)) {
      out[canonicalKey] = out[aliasKey]!;
    }
  }

  // Canonical fallback chain: if specific role is missing, inherit from broader role
  if (!out.subheading && out.body) out.subheading = out.body;
  if (!out.section && out.heading) out.section = out.heading;
  if (!out.item && out.body) out.item = out.body;
  if (!out.price && out.body) out.price = out.body;

  return out;
}

/**
 * Resolves and normalizes template variables for a given theme mode.
 * Merges base variables with theme variant overrides, normalizes legacy keys
 * to canonical keys, and returns a fully typed ResolvedTemplateVariables object.
 *
 * Drop-in replacement for the old `resolveTemplateVariables` , same signature,
 * richer return type. Old `${colors.anyOldKey}` bindings still resolve because
 * we keep ALL original keys in the output alongside the canonical ones.
 */
export function resolveTemplateVariables(
  template: Template,
  mode: TemplateThemeMode,
): ResolvedTemplateVariables {
  const base: Variables = template.variables || {};
  const variant: Variables = template.themeVariants?.[mode] || {};

  // Merge base + variant for each namespace
  const rawColors = { ...(base.colors || {}), ...(variant.colors || {}) };
  const rawFonts = { ...(base.fonts || {}), ...(variant.fonts || {}) };
  const rawSizes = { ...(base.sizes || {}), ...(variant.sizes || {}) };
  const rawImages = { ...(base.images || {}), ...(variant.images || {}) };
  const rawTokens = { ...(base.tokens || {}), ...(variant.tokens || {}) };

  return {
    colors: normalizeColorRecord(rawColors as Record<string, string>),
    fonts: normalizeFontRecord(rawFonts as Record<string, string>),
    sizes: { ...rawSizes } as ResolvedTemplateVariables["sizes"],
    images: { ...rawImages } as ResolvedTemplateVariables["images"],
    tokens: { ...rawTokens },
    // Skin markers live at the top level of `variables`, not in a namespace, so
    // they are copied explicitly (variant wins over base, like the namespaces).
    skin: variant.skin ?? base.skin,
    headline: variant.headline ?? base.headline,
    skinDecor: variant.skinDecor ?? base.skinDecor,
  };
}

// ---------------------------------------------------------------------------
// 4. CSS CUSTOM PROPERTY INJECTION
// ---------------------------------------------------------------------------

/**
 * Converts canonical variable keys to CSS custom property names.
 *
 * colors.heading      → --mnrq-color-heading
 * fonts.body          → --mnrq-font-body
 * sizes.borderRadius  → --mnrq-size-border-radius
 * tokens.myToken      → --mnrq-token-my-token
 */
function camelToKebab(str: string): string {
  return str.replace(/([A-Z])/g, (char) => `-${char.toLowerCase()}`);
}

function buildCssVarName(namespace: string, key: string): string {
  return `--mnrq-${namespace}-${camelToKebab(key)}`;
}

/**
 * Builds a React.CSSProperties-compatible object of CSS custom properties
 * derived from the resolved variables. Inject this onto the renderer root div
 * alongside TEMPLATE_THEME_SURFACE_VARS so external components can consume
 * the template palette via standard CSS variables.
 *
 * Example output:
 *   { "--mnrq-color-heading": "#1a1a1a", "--mnrq-font-body": "'Inter', sans-serif", ... }
 */
export function buildTemplateCssVars(
  variables: ResolvedTemplateVariables,
): React.CSSProperties {
  const cssVars: Record<string, string> = {};

  for (const [key, value] of Object.entries(variables.colors)) {
    if (value) cssVars[buildCssVarName("color", key)] = value;
  }

  for (const [key, value] of Object.entries(variables.fonts)) {
    if (value) cssVars[buildCssVarName("font", key)] = value;
  }

  for (const [key, value] of Object.entries(variables.sizes)) {
    if (value != null) cssVars[buildCssVarName("size", key)] = String(value);
  }

  for (const [key, value] of Object.entries(variables.images)) {
    if (value) cssVars[buildCssVarName("image", key)] = value;
  }

  for (const [key, value] of Object.entries(variables.tokens)) {
    if (value != null) cssVars[buildCssVarName("token", key)] = String(value);
  }

  return cssVars as React.CSSProperties;
}

/**
 * Returns a typed map of CSS variable names for a given namespace so
 * external components can reference them without hardcoding the prefix.
 *
 * Usage:
 *   const colorVars = getCssVarNames("color", variables.colors);
 *   style={{ color: `var(${colorVars.heading})` }}
 */
export function getCssVarNames<T extends Record<string, unknown>>(
  namespace: "color" | "font" | "size" | "image" | "token",
  record: T,
): Record<keyof T, string> {
  const out = {} as Record<keyof T, string>;
  for (const key of Object.keys(record) as Array<keyof T>) {
    out[key] = `var(${buildCssVarName(namespace, key as string)})`;
  }
  return out;
}

// ---------------------------------------------------------------------------
// 5. TEMPLATE THEME CONTEXT + HOOK
// ---------------------------------------------------------------------------

export interface TemplateThemeContextValue {
  /** Fully resolved, normalized variables for the active theme mode */
  variables: ResolvedTemplateVariables;
  /** Active theme mode ("light" | "dark") */
  mode: TemplateThemeMode;
  /**
   * Typed CSS variable name helpers , use these in external components
   * instead of hardcoding "--mnrq-color-heading" strings.
   *
   * Example:
   *   style={{ color: `var(${cssVars.colors.heading})` }}
   */
  cssVars: {
    colors: Record<string, string>;
    fonts: Record<string, string>;
    sizes: Record<string, string>;
    images: Record<string, string>;
    tokens: Record<string, string>;
  };
  /**
   * Resolves a ${token} string using the current variables.
   * Useful for external components that store raw token strings.
   *
   * Example:
   *   resolveToken("${colors.accent}") → "#ff5a3c"
   */
  resolveToken: (tokenString: string) => string;
}

/**
 * Builds the context value from resolved variables + mode.
 * Call this inside the renderer to construct the provider value.
 */
export function buildTemplateThemeContextValue(
  variables: ResolvedTemplateVariables,
  mode: TemplateThemeMode,
): TemplateThemeContextValue {
  const cssVars = {
    colors: getCssVarNames("color", variables.colors),
    fonts: getCssVarNames("font", variables.fonts),
    sizes: getCssVarNames("size", variables.sizes as Record<string, unknown>),
    images: getCssVarNames("image", variables.images as Record<string, unknown>),
    tokens: getCssVarNames("token", variables.tokens),
  };

  const resolveToken = (tokenString: string): string => {
    if (typeof tokenString !== "string" || !tokenString.includes("${")) {
      return tokenString;
    }
    return tokenString.replace(/\$\{([^}]+)\}/g, (_, expr) => {
      const parts = expr.trim().split(".");
      let value: any = variables;
      for (const part of parts) {
        if (value && typeof value === "object" && part in value) {
          value = value[part];
        } else {
          return "";
        }
      }
      return value != null ? String(value) : "";
    });
  };

  return { variables, mode, cssVars, resolveToken };
}

// ---------------------------------------------------------------------------
// 6. UPDATED resolveTemplateTokenString
//    (backward compat , same signature as the old one in util.ts)
// ---------------------------------------------------------------------------

/**
 * Resolves ${namespace.key} token strings against a ResolvedTemplateVariables
 * object. Works with both the new canonical keys and any legacy keys that were
 * preserved in the output.
 *
 * Replaces the old resolveTemplateTokenString(value, variables) from util.ts.
 * The signature is intentionally identical so call sites don't need changing.
 */
export function resolveTemplateTokenString(
  value: string,
  variables: Record<string, any>,
): string {
  if (typeof value !== "string" || !value.includes("${")) return value;

  return value.replace(/\$\{([^}]+)\}/g, (_, expr) => {
    const parts = (expr as string).trim().split(".");
    let current: any = variables;
    for (const part of parts) {
      if (current && typeof current === "object" && part in current) {
        current = current[part];
      } else {
        return "";
      }
    }
    return current != null ? String(current) : "";
  });
}

// ---------------------------------------------------------------------------
// 7. CANONICAL DEFAULTS
//    Safe fallback values used when a template defines no variables at all.
// ---------------------------------------------------------------------------

export const CANONICAL_COLOR_DEFAULTS: CanonicalColors = {
  background: "#ffffff",
  foreground: "#0f0f0f",
  panel: "#ffffff",
  panelBorder: "rgba(15,15,15,0.12)",
  divider: "rgba(15,15,15,0.12)",
  muted: "#f5f5f5",
  mutedForeground: "#555555",
  heading: "#111827",
  subheading: "#526172",
  body: "#374151",
  itemTitle: "#111827",
  itemDesc: "#526172",
  sectionTitle: "#1f2937",
  price: "#111827",
  primary: "#0f0f0f",
  primaryForeground: "#ffffff",
  secondary: "#f5f5f5",
  secondaryForeground: "#161616",
  accent: "#ff5a3c",
  accentForeground: "#ffffff",
  promoBg: "rgba(255,90,60,0.12)",
  promoText: "#9a3412",
  promoBorder: "rgba(255,90,60,0.24)",
  destructive: "#d73939",
  destructiveForeground: "#ffffff",
};

export const CANONICAL_FONT_DEFAULTS: CanonicalFonts = {
  heading: "'Inter', sans-serif",
  subheading: "'Inter', sans-serif",
  body: "'Inter', sans-serif",
  section: "'Inter', sans-serif",
  item: "'Inter', sans-serif",
  price: "'Inter', sans-serif",
};

/**
 * Returns canonical defaults merged with whatever the template provides.
 * Use this when you need a guaranteed full palette (e.g. in the paged-flip
 * palette builder) without having to null-check every key.
 */
export function resolveCanonicalVariables(
  template: Template,
  mode: TemplateThemeMode,
): ResolvedTemplateVariables {
  const resolved = resolveTemplateVariables(template, mode);
  return {
    ...resolved,
    colors: { ...CANONICAL_COLOR_DEFAULTS, ...resolved.colors },
    fonts: { ...CANONICAL_FONT_DEFAULTS, ...resolved.fonts },
  };
}